// LiveResults.jsx

import React, { useMemo, useState } from 'react';
import { useLiveResults } from '../_shared/hooks/useLiveResults';
import './LiveResults.css';

const formatearFecha = (fecha) => {
  if (!fecha) return 'Sin información';
  const fechaValida = new Date(fecha);
  if (Number.isNaN(fechaValida.getTime())) return 'Fecha no válida';
  return fechaValida.toLocaleString('es-BO', {
    dateStyle: 'medium',
    timeStyle: 'medium',
    timeZone: 'America/La_Paz'
  });
};

const obtenerOpciones = (resultados, campo) => {
  return [
    ...new Set(
      resultados
        .map((resultado) => resultado?.[campo])
        .filter((valor) =>
          valor !== null &&
          valor !== undefined &&
          String(valor).trim() !== ''
        )
        .map((valor) => String(valor))
    )
  ].sort((a, b) =>
    a.localeCompare(b, 'es', { numeric: true, sensitivity: 'base' })
  );
};

const compararNumeros = (a, b) => {
  const parsear = (val) => {
    if (val === null || val === undefined) return Infinity;
    const str = String(val).trim();
    if (str === '') return Infinity;
    const num = Number(str);
    return Number.isFinite(num) ? num : Infinity;
  };

  const numeroA = parsear(a);
  const numeroB = parsear(b);

  if (numeroA === Infinity && numeroB === Infinity) return 0;

  return numeroA - numeroB;
};

const compararDistancias = (a, b) => {
  const extraerKm = (val) => {
    if (val === null || val === undefined) return Infinity;
    const match = String(val).match(/(\d+(?:\.\d+)?)/);
    return match ? Number(match[1]) : Infinity;
  };
  return extraerKm(a) - extraerKm(b);
};

const convertirTiempo = (tiempo) => {
  if (tiempo === null || tiempo === undefined) return Infinity;

  const valor = String(tiempo).trim();
  if (!valor) return Infinity;

  const partes = valor.split(':');
  if (partes.length >= 2) {
    const segundos = partes.reduce(
      (total, parte) => total * 60 + Number(parte),
      0
    );
    return Number.isFinite(segundos) ? segundos : Infinity;
  }

  const numero = Number(valor);
  return Number.isFinite(numero) ? numero : Infinity;
};

const LiveResults = ({ slug, nombreCompetencia = 'Resultados en vivo' }) => {
  const {
    // Categorías
    resultados,
    disponible,
    cantidad,
    actualizadoEn,

    // General
    resultadosGeneral,
    disponibleGeneral,
    cantidadGeneral,
    generalActualizadoEn,

    // Compartido
    cargando,
    error,
    conexion
  } = useLiveResults(slug);

  // 🆕 Modo de vista: 'categorias' | 'general'
  const [viewMode, setViewMode] = useState('categorias');

  const [busqueda, setBusqueda] = useState('');
  const [distancia, setDistancia] = useState('');
  const [genero, setGenero] = useState('');
  const [categoria, setCategoria] = useState('');
  const [orden, setOrden] = useState('posicion');

  // Base de datos según modo
  const resultadosBase = viewMode === 'general' ? resultadosGeneral : resultados;
  const disponibleActual =
    viewMode === 'general' ? disponibleGeneral : disponible;
  const cantidadActual =
    viewMode === 'general' ? cantidadGeneral : cantidad;
  const actualizadoEnActual =
    viewMode === 'general' ? generalActualizadoEn : actualizadoEn;

  const distancias = useMemo(
    () => obtenerOpciones(resultadosBase, 'distancia'),
    [resultadosBase]
  );

  const generos = useMemo(
    () => obtenerOpciones(resultadosBase, 'genero'),
    [resultadosBase]
  );

  const categorias = useMemo(
    () => obtenerOpciones(resultadosBase, 'categoria'),
    [resultadosBase]
  );

  // 🆕 Cambio de modo que fuerza un género concreto en General
  // para evitar mezcla de Damas y Varones con posiciones repetidas (1,1)
  const handleViewModeChange = (nuevoModo) => {
    setViewMode(nuevoModo);

    if (nuevoModo === 'general') {
      // Si aún no se eligió género, auto-elegimos el primero disponible
      if (!genero) {
        const generosBase = obtenerOpciones(resultadosGeneral, 'genero');
        setGenero(generosBase[0] || 'Damas');
      }
    } else {
      // En Categorías, el filtro de categoría ya separa por género.
      setGenero('');
      setCategoria('');
    }
  };

  const resultadosFiltrados = useMemo(() => {
    const termino = busqueda.trim().toLowerCase();

    const filtrados = resultadosBase.filter((resultado) => {
      const coincideDistancia =
        !distancia || String(resultado.distancia ?? '') === distancia;

      // En General el género es obligatorio; en Categorías es opcional.
      const coincideGenero =
        viewMode === 'general'
          ? String(resultado.genero ?? '') === genero
          : !genero || String(resultado.genero ?? '') === genero;

      const coincideCategoria =
        !categoria || String(resultado.categoria ?? '') === categoria;

      const coincideBusqueda =
        !termino ||
        String(resultado.dorsal ?? '').toLowerCase().includes(termino) ||
        String(resultado.nombre ?? '').toLowerCase().includes(termino);

      return (
        coincideDistancia &&
        coincideGenero &&
        coincideCategoria &&
        coincideBusqueda
      );
    });

    return filtrados.sort((a, b) => {
      if (orden === 'dorsal') {
        return compararNumeros(a.dorsal, b.dorsal);
      }

      if (orden === 'tiempo') {
        return convertirTiempo(a.tiempo) - convertirTiempo(b.tiempo);
      }

      // Orden por defecto: posición
      // 1) Distancia asc
      // 2) En Categorías: categoría asc; en General: se omite
      // 3) Posición asc
      const cmpDistancia = compararDistancias(a.distancia, b.distancia);
      if (cmpDistancia !== 0) return cmpDistancia;

      if (viewMode === 'categorias') {
        const cmpCategoria = String(a.categoria ?? '')
          .localeCompare(String(b.categoria ?? ''), 'es', {
            sensitivity: 'base'
          });
        if (cmpCategoria !== 0) return cmpCategoria;
      }

      return compararNumeros(a.posicion, b.posicion);
    });
  }, [resultadosBase, busqueda, distancia, genero, categoria, orden, viewMode]);

  const limpiarFiltros = () => {
    setBusqueda('');
    setDistancia('');
    setGenero('');
    setCategoria('');
    setOrden('posicion');
  };

  const estadoConexion = {
    conectado: { texto: 'En vivo', clase: 'live-results__connection--connected' },
    reconectando: { texto: 'Reconectando...', clase: 'live-results__connection--reconnecting' },
    desconectado: { texto: 'Desconectado', clase: 'live-results__connection--disconnected' },
    conectando: { texto: 'Conectando...', clase: 'live-results__connection--reconnecting' }
  };

  const estadoActual = estadoConexion[conexion] || estadoConexion.conectando;

  // Determinar si agrupar por categoría (depende del modo)
  const agruparPorCategoria = viewMode === 'categorias';

  return (
    <main className="live-results">
      <header className="live-results__header">
        <div className="live-results__header-content">
          <span className="live-results__eyebrow">CRONOMETRAJE OFICIAL</span>
          <h1>{nombreCompetencia}</h1>
          <p>Sigue los resultados y tiempos de los atletas en directo.</p>
        </div>

        <div className={`live-results__connection ${estadoActual.clase}`}>
          <span className="live-results__connection-dot" />
          {estadoActual.texto}
        </div>
      </header>

      {/* 🆕 Tabs Categorías | General */}
      <div className="live-results__viewmode-tabs">
        <button
          type="button"
          className={`live-results__viewmode-tab ${
            viewMode === 'categorias' ? 'active' : ''
          }`}
          onClick={() => handleViewModeChange('categorias')}
        >
          Por Categorías
        </button>
        <button
          type="button"
          className={`live-results__viewmode-tab ${
            viewMode === 'general' ? 'active' : ''
          }`}
          onClick={() => handleViewModeChange('general')}
        >
          General
        </button>
      </div>

      {/* 🆕 Aviso explicativo solo en General */}
      {viewMode === 'general' && (
        <p className="live-results__general-hint">
          La clasificación general se calcula por género. Selecciona Damas o Varones para ver la posición real.
        </p>
      )}

      <section className="live-results__summary">
        <div className="live-results__summary-card">
          <span>Resultados recibidos</span>
          <strong>{cantidadActual}</strong>
        </div>
        <div className="live-results__summary-card">
          <span>Resultados visibles</span>
          <strong>{resultadosFiltrados.length}</strong>
        </div>
        <div className="live-results__summary-card live-results__summary-card--updated">
          <span>Última actualización</span>
          <strong>{formatearFecha(actualizadoEnActual)}</strong>
        </div>
      </section>

      <section className="live-results__filters">
        <div className="live-results__filters-heading">
          <div>
            <h2>Resultados de la carrera</h2>
            <p>Filtra y encuentra rápidamente a los participantes.</p>
          </div>
          <button
            type="button"
            onClick={limpiarFiltros}
            className="live-results__clear"
          >
            Limpiar filtros
          </button>
        </div>

        <div className="live-results__filter-grid">
          <div className="live-results__field live-results__field--search">
            <label htmlFor="live-search">Buscar atleta</label>
            <input
              id="live-search"
              type="search"
              placeholder="Nombre o número de dorsal..."
              value={busqueda}
              onChange={(event) => setBusqueda(event.target.value)}
            />
          </div>

          <div className="live-results__field">
            <label htmlFor="live-distance">Distancia</label>
            <select
              id="live-distance"
              value={distancia}
              onChange={(event) => setDistancia(event.target.value)}
            >
              <option value="">Todas</option>
              {distancias.map((valor) => (
                <option key={valor} value={valor}>{valor}</option>
              ))}
            </select>
          </div>

          {/* 🆕 Género: en General es obligatorio (sin opción "Todos") */}
          <div className="live-results__field">
            <label htmlFor="live-gender">
              Género {viewMode === 'general' && <span style={{ color: '#dc2626' }}>*</span>}
            </label>
            <select
              id="live-gender"
              value={genero}
              onChange={(event) => setGenero(event.target.value)}
            >
              {viewMode === 'categorias' && <option value="">Todos</option>}
              {generos.map((valor) => (
                <option key={valor} value={valor}>{valor}</option>
              ))}
            </select>
          </div>

          {/* 🆕 Filtro Categoría solo en modo Categorías */}
          {agruparPorCategoria && (
            <div className="live-results__field">
              <label htmlFor="live-category">Categoría</label>
              <select
                id="live-category"
                value={categoria}
                onChange={(event) => setCategoria(event.target.value)}
              >
                <option value="">Todas</option>
                {categorias.map((valor) => (
                  <option key={valor} value={valor}>{valor}</option>
                ))}
              </select>
            </div>
          )}

          <div className="live-results__field">
            <label htmlFor="live-order">Ordenar por</label>
            <select
              id="live-order"
              value={orden}
              onChange={(event) => setOrden(event.target.value)}
            >
              <option value="posicion">Posición</option>
              <option value="dorsal">Dorsal</option>
              <option value="tiempo">Tiempo</option>
            </select>
          </div>
        </div>
      </section>

      {error && <div className="live-results__alert">{error}</div>}

      {cargando ? (
        <div className="live-results__empty">
          <span className="live-results__loader" />
          <p>Conectando con el sistema de cronometraje...</p>
        </div>
      ) : !disponibleActual || resultadosBase.length === 0 ? (
        <div className="live-results__empty">
          <span className="live-results__empty-icon">⏱</span>
          <h3>Esperando resultados</h3>
          <p>Los resultados aparecerán aquí cuando estén disponibles.</p>
        </div>
      ) : resultadosFiltrados.length === 0 ? (
        <div className="live-results__empty">
          <h3>No se encontraron resultados</h3>
          <p>Prueba modificando los filtros de búsqueda.</p>
        </div>
      ) : (
        <section className="live-results__table-section">
          <div className="live-results__table-info">
            <span>
              Mostrando <strong>{resultadosFiltrados.length}</strong>{' '}
              de <strong>{resultadosBase.length}</strong> resultados
            </span>
            <span className="live-results__live-label">
              ACTUALIZACIÓN EN DIRECTO
            </span>
          </div>

          <div className="live-results__table-wrapper">
            <table className="live-results__table">
              <thead>
                <tr>
                  <th>Posición</th>
                  <th>Dorsal</th>
                  <th>Nombre</th>
                  <th>Distancia</th>
                  <th>Género</th>
                  {agruparPorCategoria && <th>Categoría</th>}
                  <th>Tiempo</th>
                  <th>Estado</th>
                </tr>
              </thead>

              <tbody>
                {resultadosFiltrados.map((resultado, index) => {
                  const prev = resultadosFiltrados[index - 1];
                  const nuevoGrupo =
                    !prev ||
                    prev.distancia !== resultado.distancia ||
                    (agruparPorCategoria &&
                      prev.categoria !== resultado.categoria);

                  return (
                    <React.Fragment
                      key={`${resultado.dorsal}-${resultado.distancia}-${index}`}
                    >
                      {nuevoGrupo && (
                        <tr className="live-results__group-header">
                          <td colSpan={agruparPorCategoria ? 8 : 7}>
                            <span className="live-results__group-header-badge">
                              {resultado.distancia || '—'}
                            </span>
                            {agruparPorCategoria && (
                              <span className="live-results__group-header-title">
                                {resultado.categoria || 'Sin categoría'}
                              </span>
                            )}
                          </td>
                        </tr>
                      )}

                      <tr>
                        <td>
                          <span className="live-results__position">
                            {resultado.posicion ?? '—'}
                          </span>
                        </td>
                        <td>
                          <strong className="live-results__bib">
                            {resultado.dorsal || '—'}
                          </strong>
                        </td>
                        <td className="live-results__athlete">
                          {resultado.nombre || 'Sin nombre'}
                        </td>
                        <td>{resultado.distancia || '—'}</td>
                        <td>{resultado.genero || '—'}</td>
                        {agruparPorCategoria && (
                          <td>{resultado.categoria || '—'}</td>
                        )}
                        <td>
                          <strong className="live-results__time">
                            {resultado.tiempo || '—'}
                          </strong>
                        </td>
                        <td>
                          <span className="live-results__status">
                            {resultado.estado || '—'}
                          </span>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <footer className="live-results__footer">
        <span>Resultados provisionales proporcionados por Race Result.</span>
        <span>InnovateSport · Cronometraje en vivo</span>
      </footer>
    </main>
  );
};

export default LiveResults;